import { useState, useEffect, useMemo } from 'react';
import { gruposLibranzas } from '../services/api';
import { useToast } from '../context/ToastContext';
import { UserGroupIcon, ArrowPathIcon, LockClosedIcon, LockOpenIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import ConfirmModal from '../components/ConfirmModal';
import InputModal from '../components/InputModal';

const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const weekdayNames = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

// Available years for selection
const AVAILABLE_YEARS = [2024, 2025, 2026, 2027];

export default function GruposLibranzas() {
    const [grupos, setGrupos] = useState([]);
    const [selectedGrupo, setSelectedGrupo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedYear, setSelectedYear] = useState(2026);
    const [tipoFilter, setTipoFilter] = useState('bei');

    // State for manual selection
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedDates, setSelectedDates] = useState(new Set());
    const [currentMonth, setCurrentMonth] = useState(0); // 0-11
    const [currentYear, setCurrentYear] = useState(2026);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, grupo: null });
    const [isInputModalOpen, setIsInputModalOpen] = useState(false);

    const { success, error: toastError } = useToast();

    useEffect(() => {
        loadGrupos();
        setCurrentYear(selectedYear); // Sync calendar year with selected year
    }, [selectedYear, tipoFilter]);

    const loadGrupos = async () => {
        try {
            setLoading(true);
            setSelectedGrupo(null);
            const res = await gruposLibranzas.getAll(selectedYear, tipoFilter);
            setGrupos(res.grupos || []);
        } catch (error) {
            toastError('Error al cargar grupos: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectGrupo = (grupo) => {
        setSelectedGrupo(grupo);
        setIsEditMode(false);
        if (grupo.configuracion_libranza && grupo.configuracion_libranza.fechas) {
            setSelectedDates(new Set(grupo.configuracion_libranza.fechas));
        } else {
            setSelectedDates(new Set());
        }
    };

    const handleAddGroup = () => {
        setIsInputModalOpen(true);
    };

    const confirmAddGroup = async (nombre) => {
        try {
            await gruposLibranzas.create({ nombre, tipo: tipoFilter });
            success('Grupo creado');
            loadGrupos();
        } catch (error) {
            toastError('Error al crear grupo: ' + error.message);
        }
    };

    const handleDeleteGroup = (e, grupo) => {
        e.stopPropagation(); // Prevent selection
        setDeleteConfirm({ isOpen: true, grupo });
    };

    const confirmDeleteGroup = async () => {
        const grupo = deleteConfirm.grupo;
        if (!grupo) return;

        try {
            await gruposLibranzas.delete(grupo.id);
            success('Grupo eliminado');
            setGrupos(prev => prev.filter(g => g.id !== grupo.id));
            if (selectedGrupo?.id === grupo.id) {
                setSelectedGrupo(null);
            }
        } catch (error) {
            toastError('Error al eliminar grupo: ' + error.message);
        }
    };

    const toggleDate = (dateStr) => {
        if (!isEditMode) return;
        const newSet = new Set(selectedDates);
        if (newSet.has(dateStr)) {
            newSet.delete(dateStr);
        } else {
            newSet.add(dateStr);
        }
        setSelectedDates(newSet);
    };

    const handleSave = async () => {
        if (!selectedGrupo) return;
        try {
            const configPayload = {
                tipo: 'manual',
                fechas: Array.from(selectedDates).sort()
            };
            await gruposLibranzas.update(selectedGrupo.id, {
                configuracion_libranza: configPayload
            });
            success('Calendario de libranzas guardado');
            setGrupos(prev => prev.map(g =>
                g.id === selectedGrupo.id
                    ? { ...g, configuracion_libranza: configPayload }
                    : g
            ));
            setIsEditMode(false);
        } catch (error) {
            toastError('Error al guardar: ' + error.message);
        }
    };

    const monthData = useMemo(() => {
        const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
        const firstDay = new Date(currentYear, currentMonth, 1).getDay();
        const startOffset = firstDay === 0 ? 6 : firstDay - 1;
        const days = [];

        for (let i = 0; i < startOffset; i++) {
            days.push({ key: `empty-${i}`, empty: true });
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            days.push({
                key: dateStr,
                day,
                dateStr,
                empty: false
            });
        }
        return days;
    }, [currentMonth, currentYear]);

    const changeMonth = (delta) => {
        const newDate = new Date(currentYear, currentMonth + delta, 1);
        setCurrentMonth(newDate.getMonth());
        setCurrentYear(newDate.getFullYear());
    };

    return (
        <div className="flex h-screen bg-[#0f0f23] overflow-hidden">
            {/* Sidebar */}
            <div className="w-80 bg-[#1a1a3e] border-r border-white/10 flex flex-col shrink-0">
                <div className="p-6 border-b border-white/10 flex justify-between items-start">
                    <div>
                        <h2 className="text-xl font-bold text-white flex items-center gap-2">
                            <UserGroupIcon className="w-6 h-6 text-purple-400" />
                            Grupos
                        </h2>
                        <p className="text-gray-400 text-xs mt-1">Gestión de libranzas</p>
                    </div>
                    <button
                        onClick={handleAddGroup}
                        className="p-1.5 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500 hover:text-white transition-colors"
                        title="Nuevo Grupo"
                    >
                        <PlusIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Tipo Filter */}
                <div className="px-4 pt-4">
                    <div className="flex bg-[#0f0f23] rounded-lg p-1 border border-white/10">
                        <button
                            onClick={() => setTipoFilter('bei')}
                            className={`flex-1 text-xs py-2 rounded-md transition-all font-semibold ${tipoFilter === 'bei' ? 'bg-purple-500 text-white shadow' : 'text-gray-400 hover:text-white'}`}
                        >
                            🚌 BEI
                        </button>
                        <button
                            onClick={() => setTipoFilter('servicio')}
                            className={`flex-1 text-xs py-2 rounded-md transition-all font-semibold ${tipoFilter === 'servicio' ? 'bg-blue-500 text-white shadow' : 'text-gray-400 hover:text-white'}`}
                        >
                            🚍 Servicio
                        </button>
                    </div>
                </div>

                {/* Year Selector */}
                <div className="px-4 pt-4 pb-2 border-b border-white/10">
                    <label className="block text-xs text-gray-500 mb-1">Año</label>
                    <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                        className="w-full bg-[#0f0f23] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                    >
                        {AVAILABLE_YEARS.map(year => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                    {loading ? (
                        <div className="text-center text-gray-500 py-4">Cargando...</div>
                    ) : (
                        grupos.map(grupo => (
                            <div key={grupo.id} className="relative group/item">
                                <button
                                    onClick={() => handleSelectGrupo(grupo)}
                                    className={`w-full text-left p-3 pr-10 rounded-xl transition-all duration-200 border ${selectedGrupo?.id === grupo.id
                                        ? 'bg-purple-500/20 border-purple-500/50 text-white shadow-lg shadow-purple-500/10'
                                        : 'bg-white/5 border-transparent text-gray-400 hover:bg-white/10 hover:text-white'
                                        }`}
                                >
                                    <div className="font-semibold text-sm">{grupo.nombre}</div>
                                    <div className="text-xs mt-1 opacity-70 flex items-center gap-2">
                                        <span className={`w-1.5 h-1.5 rounded-full ${grupo.configuracion_libranza ? 'bg-green-400' : 'bg-gray-600'}`}></span>
                                        {grupo.configuracion_libranza?.fechas?.length > 0
                                            ? `${grupo.configuracion_libranza.fechas.length} días`
                                            : 'Vacío'}
                                    </div>
                                </button>
                                <button
                                    onClick={(e) => handleDeleteGroup(e, grupo)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-500 hover:text-red-400 opacity-0 group-hover/item:opacity-100 transition-opacity"
                                    title="Eliminar grupo"
                                >
                                    <TrashIcon className="w-4 h-4" />
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 bg-[#1a1a3e] p-8 overflow-y-auto flex flex-col">
                {selectedGrupo ? (
                    <div className="flex-1 flex flex-col max-w-5xl mx-auto w-full">
                        {/* Header */}
                        <div className="flex justify-between items-center bg-[#1a1a3e]/90 backdrop-blur pb-6 z-20 border-b border-white/5 shrink-0">
                            <div>
                                <h1 className="text-3xl font-bold text-white mb-2">{selectedGrupo.nombre}</h1>
                                <p className="text-purple-300">Selecciona los días libres en el calendario</p>
                            </div>

                            <div className="flex items-center gap-4">
                                <div className={`flex items-center gap-3 px-4 py-2 rounded-lg border ${isEditMode ? 'bg-purple-500/10 border-purple-500/30' : 'bg-white/5 border-white/10'}`}>
                                    <label className="text-sm font-medium text-white cursor-pointer select-none">
                                        Modo Edición
                                    </label>
                                    <button
                                        onClick={() => setIsEditMode(!isEditMode)}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${isEditMode ? 'bg-purple-500' : 'bg-gray-600'}`}
                                    >
                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isEditMode ? 'translate-x-6' : 'translate-x-1'}`} />
                                    </button>
                                    {isEditMode ? <LockOpenIcon className="w-5 h-5 text-purple-400" /> : <LockClosedIcon className="w-5 h-5 text-gray-400" />}
                                </div>

                                {isEditMode && (
                                    <button
                                        onClick={handleSave}
                                        className="px-6 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold shadow-lg shadow-purple-500/20 transition-all flex items-center gap-2"
                                    >
                                        <ArrowPathIcon className="w-5 h-5" />
                                        Guardar
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Month Navigation */}
                        <div className="flex items-center justify-between py-6 shrink-0">
                            <button onClick={() => changeMonth(-1)} className="p-2 hover:bg-white/10 rounded-full text-gray-300">
                                <ChevronLeftIcon className="w-8 h-8" />
                            </button>
                            <h2 className="text-3xl font-bold text-white capitalize w-64 text-center">
                                {monthNames[currentMonth]} <span className="text-gray-500">{currentYear}</span>
                            </h2>
                            <button onClick={() => changeMonth(1)} className="p-2 hover:bg-white/10 rounded-full text-gray-300">
                                <ChevronRightIcon className="w-8 h-8" />
                            </button>
                        </div>

                        {/* Calendar Grid - Month View */}
                        <div className="bg-white/5 p-8 rounded-3xl border border-white/10 shadow-2xl mx-auto w-full max-w-3xl backdrop-blur-sm">
                            {/* Weekday Headers */}
                            <div className="grid grid-cols-7 gap-4 mb-4">
                                {weekdayNames.map(day => (
                                    <div key={day} className="text-center text-gray-400 font-semibold uppercase tracking-wider text-sm">
                                        {day}
                                    </div>
                                ))}
                            </div>

                            {/* Days Grid */}
                            <div className="grid grid-cols-7 gap-4">
                                {monthData.map(dayData => {
                                    if (dayData.empty) return <div key={dayData.key} />;

                                    const isSelected = selectedDates.has(dayData.dateStr);

                                    return (
                                        <button
                                            key={dayData.key}
                                            onClick={() => toggleDate(dayData.dateStr)}
                                            disabled={!isEditMode}
                                            className={`
                                                relative rounded-xl border-2 transition-all duration-200 flex flex-col items-center justify-center aspect-square
                                                ${!isEditMode && !isSelected ? 'opacity-50' : 'opacity-100'}
                                                ${isEditMode ? 'hover:scale-105 active:scale-95 cursor-pointer hover:border-white/30' : 'cursor-default border-transparent'}
                                                ${isSelected
                                                    ? 'bg-purple-500 border-purple-400 text-white shadow-lg shadow-purple-500/30 z-10'
                                                    : 'bg-[#0f0f23] border-white/5 text-gray-300 hover:bg-white/5'
                                                }
                                            `}
                                        >
                                            <span className="text-2xl font-bold">{dayData.day}</span>
                                            {isSelected && (
                                                <span className="text-xs uppercase tracking-wider font-semibold mt-1">Libre</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                    </div>
                ) : (
                    <div className="h-full flex flex-col items-center justify-center text-gray-500 opacity-50">
                        <UserGroupIcon className="w-32 h-32 mb-6" />
                        <p className="text-2xl font-light">Selecciona un grupo para gestionar sus libranzas</p>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, grupo: null })}
                onConfirm={confirmDeleteGroup}
                title="Eliminar Grupo"
                message={`¿Estás seguro de que deseas eliminar el grupo "${deleteConfirm.grupo?.nombre}"?`}
                confirmText="Eliminar"
                variant="danger"
            />
            {/* Add Group Modal */}
            <InputModal
                isOpen={isInputModalOpen}
                onClose={() => setIsInputModalOpen(false)}
                onConfirm={confirmAddGroup}
                title="Nuevo Grupo"
                message="Introduce el nombre para el nuevo grupo de libranzas."
                placeholder="Ej: Grupo 10"
            />
        </div>
    );
}
